import * as React from 'react'
import {
  InputBase,
  InputBaseProps,
  InputBaseRoot,
  InputBaseRootProps,
  InputBaseComponent,
  InputBaseInternalComponentProps,
} from '../InputBase'
import { Outline } from './Outline'
import styles from './index.module.scss'
import clsx from 'clsx'

// eslint-disable-next-line
export interface OutlinedInputProps extends Omit<InputBaseProps, 'classes'> {
  classes?: InputBaseProps['classes'] & {
    /** Styles applied to the fieldset */
    fieldset?: string
  }
}

export const OutlinedInput = React.forwardRef<
  HTMLDivElement,
  OutlinedInputProps
>(function OutlinedInput(props, ref) {
  const {
    fullWidth = false,
    inputComponent = 'input',
    multiline = false,
    slots = {},
    type = 'text',
    classes = {},
    ...other
  } = props

  const RootSlot = slots.root ?? OutlinedInputRoot
  const InputSlot = slots.input ?? OutlinedInputInput

  const { fieldset = '', ...otherClasses } = classes

  return (
    <InputBase
      slots={{ root: RootSlot, input: InputSlot }}
      renderSuffix={() => <Outline classes={{ root: fieldset }} />}
      fullWidth={fullWidth}
      inputComponent={inputComponent}
      multiline={multiline}
      ref={ref}
      type={type}
      classes={otherClasses}
      {...other}
    />
  )
})
// @ts-ignore - We add this property to find this component when wrapped in FormControl
OutlinedInput.factorName = 'Input'

const OutlinedInputRoot = React.forwardRef<HTMLDivElement, InputBaseRootProps>(
  ({ classes, ...others }, ref) => {
    return (
      <InputBaseRoot
        ref={ref}
        {...others}
        classes={{
          ...classes,
          root: clsx(styles.root, classes?.root),
          focused: clsx(styles.focused, classes?.focused),
          error: clsx(styles.error, classes?.error),
          disabled: clsx(styles.disabled, classes?.disabled),
          readOnly: clsx(styles.readOnly, classes?.readOnly),
        }}
      />
    )
  }
)
OutlinedInputRoot.displayName = 'OutlinedInputRoot'

const OutlinedInputInput = React.forwardRef<
  HTMLInputElement,
  InputBaseInternalComponentProps
>(({ ...others }, ref) => {
  return <InputBaseComponent ref={ref} {...others} />
})

OutlinedInputInput.displayName = 'OutlinedInputInput'
